/**
 * Timezone-aware date formatting utilities.
 *
 * All functions accept an IANA timezone string (e.g. 'America/New_York').
 * If timezone is falsy, falls back to the browser's local timezone.
 */

const DEFAULT_LOCALE = 'en-US';

function resolveTimezone(tz) {
  return tz || Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Core formatter — wraps Intl.DateTimeFormat with timezone.
 */
function formatDateTime(dateStr, timezone, options = {}) {
  if (!dateStr) return 'N/A';
  const tz = resolveTimezone(timezone);
  return new Intl.DateTimeFormat(DEFAULT_LOCALE, { timeZone: tz, ...options })
    .format(new Date(dateStr));
}

/**
 * "Jan 15" — compact month + day (ReflectionTree, RecentReflectionsCard fallback)
 */
function formatDateShort(dateStr, timezone) {
  return formatDateTime(dateStr, timezone, { month: 'short', day: 'numeric' });
}

/**
 * "Jan 15, 2025, 10:30 AM" — full date+time (ReflectionCard)
 */
function formatDateFull(dateStr, timezone) {
  return formatDateTime(dateStr, timezone, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
}

/**
 * "1/15/25, 10:30 AM" — compact date+time (AdminUsers last_active)
 */
function formatDateCompact(dateStr, timezone) {
  return formatDateTime(dateStr, timezone, {
    dateStyle: 'short', timeStyle: 'short'
  });
}

/**
 * "1/15/2025" — date only (AdminUsers created_at, AdminPrompts, SettingsPage)
 */
function formatDateOnly(dateStr, timezone) {
  return formatDateTime(dateStr, timezone, {
    year: 'numeric', month: 'numeric', day: 'numeric'
  });
}

/**
 * "1/15/2025, 10:30:45 AM" — full timestamp (NotificationLogs, AiUsageLogs, SchedulerManagement)
 */
function formatLogTimestamp(dateStr, timezone) {
  return formatDateTime(dateStr, timezone, {
    year: 'numeric', month: 'numeric', day: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit'
  });
}

/**
 * Get date parts { year, month, day } in the given timezone.
 */
function getDateParts(date, timezone) {
  const tz = resolveTimezone(timezone);
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz, year: 'numeric', month: 'numeric', day: 'numeric'
  }).formatToParts(date);

  const result = {};
  for (const p of parts) {
    if (p.type === 'year') result.year = parseInt(p.value, 10);
    if (p.type === 'month') result.month = parseInt(p.value, 10);
    if (p.type === 'day') result.day = parseInt(p.value, 10);
  }
  return result;
}

/**
 * "Today" / "Yesterday" / "3 days ago" / "Jan 15" — relative date
 * (RecentReflectionsCard)
 *
 * Compares dates in the user's timezone so that midnight boundaries
 * are respected correctly.
 */
function formatRelativeDate(dateStr, timezone) {
  if (!dateStr) return 'N/A';
  const date = new Date(dateStr);
  const now = new Date();

  const dateParts = getDateParts(date, timezone);
  const nowParts = getDateParts(now, timezone);

  // Build dates at midnight in a neutral way to compute day difference
  const dateDay = new Date(dateParts.year, dateParts.month - 1, dateParts.day);
  const nowDay = new Date(nowParts.year, nowParts.month - 1, nowParts.day);
  const diffDays = Math.round((nowDay - dateDay) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays > 1 && diffDays < 7) return `${diffDays} days ago`;
  return formatDateShort(dateStr, timezone);
}

/**
 * Get the year of a date in the user's timezone.
 * Useful for OnThisDayCard year labels.
 */
function getYearInTimezone(dateStr, timezone) {
  if (!dateStr) return new Date().getFullYear();
  return getDateParts(new Date(dateStr), timezone).year;
}

/**
 * Get the current hour (0-23) in the user's timezone.
 * Used for greeting ("Good morning/afternoon/evening").
 */
function getLocalHour(timezone) {
  const tz = resolveTimezone(timezone);
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz, hour: 'numeric', hour12: false
  }).formatToParts(new Date());
  const hourPart = parts.find(p => p.type === 'hour');
  return hourPart ? parseInt(hourPart.value, 10) : new Date().getHours();
}

/**
 * Convert a datetime-local input value (e.g. "2025-01-15T10:30")
 * from the user's timezone to a UTC ISO string.
 *
 * datetime-local inputs produce strings without timezone info.
 * We interpret them as being in the user's selected timezone.
 */
function localToUTC(datetimeLocalValue, timezone) {
  if (!datetimeLocalValue) return datetimeLocalValue;

  // Parse the datetime-local value (format: "YYYY-MM-DDTHH:MM")
  const [datePart, timePart] = datetimeLocalValue.split('T');
  if (!datePart) return datetimeLocalValue;

  const [year, month, day] = datePart.split('-').map(Number);
  const [hour, minute] = (timePart || '00:00').split(':').map(Number);

  // Create a reference date in UTC and find what time it is in the target TZ
  // to compute the offset
  const tz = resolveTimezone(timezone);
  const refDate = new Date(Date.UTC(year, month - 1, day, hour, minute));

  // Get the formatted time in the target timezone for the reference UTC time
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    year: 'numeric', month: 'numeric', day: 'numeric',
    hour: 'numeric', minute: 'numeric', hour12: false
  });
  const parts = formatter.formatToParts(refDate);
  const tzParts = {};
  for (const p of parts) {
    if (p.type === 'year') tzParts.year = parseInt(p.value, 10);
    if (p.type === 'month') tzParts.month = parseInt(p.value, 10);
    if (p.type === 'day') tzParts.day = parseInt(p.value, 10);
    if (p.type === 'hour') tzParts.hour = parseInt(p.value, 10);
    if (p.type === 'minute') tzParts.minute = parseInt(p.value, 10);
  }

  // Compute offset: what the TZ shows minus what UTC is
  const utcMinutes = refDate.getUTCHours() * 60 + refDate.getUTCMinutes();
  const tzMinutes = tzParts.hour * 60 + tzParts.minute;

  // Handle day boundary differences
  const refUTCDate = new Date(Date.UTC(refDate.getUTCFullYear(), refDate.getUTCMonth(), refDate.getUTCDate()));
  const tzDate = new Date(Date.UTC(tzParts.year, tzParts.month - 1, tzParts.day));
  const dayDiffMinutes = (tzDate - refUTCDate) / (1000 * 60);

  const offsetMinutes = dayDiffMinutes + tzMinutes - utcMinutes;

  // The user's local time is the input; subtract the offset to get UTC
  const localAsUTC = new Date(Date.UTC(year, month - 1, day, hour, minute));
  const utcTime = new Date(localAsUTC.getTime() - offsetMinutes * 60 * 1000);

  return utcTime.toISOString();
}

export {
  formatDateTime,
  formatDateShort,
  formatDateFull,
  formatDateCompact,
  formatDateOnly,
  formatLogTimestamp,
  formatRelativeDate,
  getYearInTimezone,
  getLocalHour,
  localToUTC,
  getDateParts
};
